import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';

/**
 * OperatorHUD — global overlay that signals "you are operating a high-level
 * diagnostic tool". Renders:
 *   • 4 corner brackets fixed at viewport corners
 *   • Vertical centerline + horizontal 1/3 + 2/3 thin rules (very subtle)
 *   • Continuous scan line sweeping top → bottom
 *   • Top-left telemetry readout (coords, viewport size, timestamp)
 *
 * z-index = 45 (above most content, below SceneHUD/40? → set 38 so it
 * doesn't steal clicks on Navigation which is z-50).
 */
const bracket = (pos) => {
  const styles = {
    tl: { top: 10, left: 10, borderTop: '1px solid', borderLeft: '1px solid' },
    tr: { top: 10, right: 10, borderTop: '1px solid', borderRight: '1px solid' },
    bl: { bottom: 10, left: 10, borderBottom: '1px solid', borderLeft: '1px solid' },
    br: { bottom: 10, right: 10, borderBottom: '1px solid', borderRight: '1px solid' },
  };
  return { position: 'fixed', width: 14, height: 14, borderColor: 'var(--hyper-cyan, #00F0FF)', opacity: 0.45, ...styles[pos] };
};

export default function OperatorHUD() {
  const [coords, setCoords] = useState({ x: 0, y: 0 });
  const [now, setNow] = useState('');
  const [vp, setVp] = useState({ w: 0, h: 0 });

  useEffect(() => {
    const mm = (e) => setCoords({ x: e.clientX, y: e.clientY });
    const rs = () => setVp({ w: window.innerWidth, h: window.innerHeight });
    rs();
    window.addEventListener('mousemove', mm, { passive: true });
    window.addEventListener('resize', rs);
    const t = setInterval(() => {
      const d = new Date();
      setNow(`${String(d.getUTCHours()).padStart(2,'0')}:${String(d.getUTCMinutes()).padStart(2,'0')}:${String(d.getUTCSeconds()).padStart(2,'0')} UTC`);
    }, 1000);
    return () => {
      window.removeEventListener('mousemove', mm);
      window.removeEventListener('resize', rs);
      clearInterval(t);
    };
  }, []);

  return (
    <div
      className="fixed inset-0 pointer-events-none z-[38]"
      aria-hidden="true"
      data-testid="operator-hud"
    >
      {/* corner brackets */}
      <span style={bracket('tl')} />
      <span style={bracket('tr')} />
      <span style={bracket('bl')} />
      <span style={bracket('br')} />

      {/* subtle cross-rules */}
      <div className="absolute inset-y-0 left-1/2 w-px bg-[var(--hyper-cyan)] opacity-[0.04]" />
      <div className="absolute inset-x-0 top-1/3 h-px bg-[var(--hyper-cyan)] opacity-[0.04]" />
      <div className="absolute inset-x-0 top-2/3 h-px bg-[var(--hyper-cyan)] opacity-[0.04]" />

      {/* continuous scan sweep */}
      <motion.div
        className="absolute inset-x-0 h-[2px] pointer-events-none"
        style={{
          background: 'linear-gradient(to right, transparent 0%, rgba(0,240,255,0.0) 10%, rgba(0,240,255,0.5) 50%, rgba(0,240,255,0.0) 90%, transparent 100%)',
          filter: 'blur(0.5px)',
        }}
        initial={{ y: '-4%' }}
        animate={{ y: '104vh' }}
        transition={{ duration: 6.5, repeat: Infinity, ease: 'linear' }}
      />

      {/* top-left telemetry readout */}
      <div className="absolute top-3 left-7 font-mono text-[9px] sm:text-[10px] tracking-[0.22em] uppercase text-white/40 leading-[1.4] hidden sm:block">
        <div><span style={{ color: 'var(--hyper-cyan)' }}>●</span> operator // online</div>
        <div>vp {vp.w}×{vp.h}</div>
        <div>crs {String(coords.x).padStart(4,'0')},{String(coords.y).padStart(4,'0')}</div>
        <div>{now}</div>
      </div>
    </div>
  );
}
